import { AlertDialog, BodyText, HeaderText, toast } from "@/components/ui";
import { type FamilyMemberHistory, getDisplay } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { EditableRecordButton } from "../../components/EditableRecordButton";
import {
  useUpdateFamilyMemberHistory,
  useVoidFamilyMemberHistory,
} from "../network";
import { FamilyMemberHistoryForm } from "./FamilyMemberHistoryForm";

export const FamilyMemberHistoryItem = ({
  familyMemberHistory,
}: {
  familyMemberHistory: FamilyMemberHistory;
}) => {
  const { patientId } = useParams({ strict: false });
  const [editing, setEditing] = useState(false);
  const [showVoidPrompt, setShowVoidPrompt] = useState(false);

  const {
    mutate: updateFamilyMemberHistory,
    isPending: isUpdatingFamilyMemberHistory,
  } = useUpdateFamilyMemberHistory({
    familyMemberHistoryId: familyMemberHistory.id as string,
    patientId: patientId as string,
    onSuccess: () => {
      toast.success("Family history updated successfully");
      setEditing(false);
    },
  });

  const {
    mutate: voidFamilyMemberHistory,
    isPending: isVoidingFamilyMemberHistory,
  } = useVoidFamilyMemberHistory({
    familyMemberHistoryId: familyMemberHistory.id as string,
    patientId: patientId as string,
    onSuccess: () => {
      toast.success("Family history voided successfully");
      setShowVoidPrompt(false);
    },
  });

  const handleVoidFamilyMemberHistory = () => {
    setShowVoidPrompt(false);
    voidFamilyMemberHistory();
  };

  return editing ? (
    <FamilyMemberHistoryForm
      familyMemberHistory={familyMemberHistory}
      onCancel={() => {
        setEditing(false);
      }}
      onSubmit={(data) => {
        updateFamilyMemberHistory({
          relationshipCode: data.relationshipCode,
          conditionCodes: data.conditionCodes.map((condition) => ({
            coding: [
              {
                code: condition.code,
                display: condition.display,
                system: condition.system,
              },
            ],
            text: condition.display,
          })),
        });
      }}
      isSubmitting={isUpdatingFamilyMemberHistory}
    />
  ) : (
    <>
      <EditableRecordButton
        onClick={() => {
          setEditing(true);
        }}
        onDelete={() => setShowVoidPrompt(true)}
        deletePending={isVoidingFamilyMemberHistory}
        containerClassName="w-full"
      >
        <div className="flex flex-row gap-2 w-full">
          <div className="flex flex-1 flex-col gap-1">
            <HeaderText size="sm">Relationship</HeaderText>
            <BodyText size="sm">
              {getDisplay(familyMemberHistory.relationship) || "Unknown"}
            </BodyText>
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <HeaderText size="sm">Condition</HeaderText>
            <BodyText size="sm">
              {familyMemberHistory.condition
                ?.map((condition) => getDisplay(condition.code) || "Unknown")
                .join(", ")}
            </BodyText>
          </div>
        </div>
      </EditableRecordButton>
      <AlertDialog
        isOpen={showVoidPrompt}
        onOpenChange={setShowVoidPrompt}
        isControlled
        title="Void Family History"
        body={`Voiding will permanently remove this family history from the patient's medical record, and should only be used if the history was added in error (i.e. charted on the wrong patient).`}
        actionLabel="Void"
        cancelLabel="Cancel"
        onAction={handleVoidFamilyMemberHistory}
        actionButtonVariant="danger"
        onCancel={() => {
          setShowVoidPrompt(false);
        }}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </>
  );
};
